import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  User, 
  Calendar, 
  MessageSquare, 
  Clock, 
  Users,
  AlertCircle,
  MessageCircle
} from 'lucide-react';

interface TelegramMessageDetails {
  id: number;
  telegram_user_id: number;
  telegram_username: string | null;
  chat_id: number;
  chat_type: string;
  chat_name: string | null;
  user_message: string;
  assistant_response: string | null;
  createdAt: string;
  response_time_ms: number | null;
}

const AdminTelegramMessageDetails = () => {
  const { messageId } = useParams<{ messageId: string }>();
  const navigate = useNavigate();
  const [message, setMessage] = useState<TelegramMessageDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (messageId) {
      fetchMessageDetails(parseInt(messageId));
    }
  }, [messageId]);

  const fetchMessageDetails = async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/api/telegram-messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          operation: 'get_details',
          message_id: id
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch message details');
      }

      const data = await response.json();
      setMessage(data.message);
    } catch (error) {
      console.error('Error fetching message details:', error);
      setError('Failed to load message details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('sr-RS', {
        timeZone: 'Europe/Belgrade',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch {
      return dateString;
    }
  };

  const getChatTypeInfo = (chatType: string) => {
    if (chatType === 'private') {
      return {
        icon: <User className="h-4 w-4" />,
        label: 'Private Chat',
        variant: 'secondary' as const
      };
    }
    return {
      icon: <Users className="h-4 w-4" />,
      label: chatType === 'supergroup' ? 'Supergroup' : 'Group',
      variant: 'outline' as const
    };
  };

  if (loading) {
    return (
      <div>
        <Button variant="ghost" onClick={() => navigate('/admin/chat-logs?tab=telegram')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Telegram Messages
        </Button>
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-64" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !message) {
    return (
      <div>
        <Button variant="ghost" onClick={() => navigate('/admin/chat-logs?tab=telegram')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Telegram Messages
        </Button>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <p>{error || 'Message not found'}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const chatTypeInfo = getChatTypeInfo(message.chat_type);

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/admin/chat-logs?tab=telegram')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Telegram Messages
        </Button>
        
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="space-y-2 flex-1">
                <div className="flex items-center gap-3">
                  <MessageCircle className="h-6 w-6 text-primary" />
                  <CardTitle className="text-2xl">
                    {message.telegram_username ? `@${message.telegram_username}` : `User ${message.telegram_user_id}`}
                  </CardTitle>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    {chatTypeInfo.icon}
                    <span>{message.chat_name || 'Unknown Chat'}</span>
                    <Badge variant={chatTypeInfo.variant}>{chatTypeInfo.label}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>{formatDate(message.createdAt)}</span>
                  </div>
                  {message.response_time_ms && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>{message.response_time_ms}ms response time</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap pt-2">
                  <Badge variant="secondary">Chat ID: {message.chat_id}</Badge>
                  <Badge variant="secondary">User ID: {message.telegram_user_id}</Badge>
                </div>
              </div>
            </div>
          </CardHeader>
        </Card>
      </div>

      {/* Conversation Flow */}
      <Card>
        <CardHeader>
          <CardTitle>Conversation</CardTitle>
        </CardHeader>
        <CardContent className="bg-muted/30 min-h-[400px]">
          <div className="space-y-4 py-4">
            {/* User Message */}
            <div className="flex justify-end">
              <div className="max-w-[80%] ml-12">
                <div className="rounded-lg p-4 shadow-sm bg-blue-500 text-white rounded-tr-none">
                  <div className="flex items-center gap-2 mb-2 text-xs text-blue-100">
                    <div className="flex items-center gap-1.5">
                      <User className="h-4 w-4" />
                      <span className="font-semibold">User Message</span>
                      <Badge variant="outline" className="ml-1 text-xs bg-blue-400/20 text-blue-100 border-blue-300">
                        {message.telegram_username ? `@${message.telegram_username}` : `ID: ${message.telegram_user_id}`}
                      </Badge>
                    </div>
                    <span className="ml-auto">
                      {formatDate(message.createdAt).split(',')[1]?.trim()}
                    </span>
                  </div>
                  <pre className="whitespace-pre-wrap break-words font-sans text-sm">
                    {message.user_message}
                  </pre>
                </div>
                <div className="text-xs text-muted-foreground mt-1 px-2 text-right">
                  {formatDate(message.createdAt)}
                </div>
              </div>
            </div>

            {/* Assistant Response */}
            {message.assistant_response ? (
              <div className="flex justify-start">
                <div className="max-w-[80%] mr-12">
                  <div className="rounded-lg p-4 shadow-sm bg-white dark:bg-gray-800 rounded-tl-none border">
                    <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <MessageSquare className="h-4 w-4" />
                        <span className="font-semibold">Assistant Response</span>
                      </div>
                      {message.response_time_ms && (
                        <span className="ml-auto">
                          {message.response_time_ms}ms
                        </span>
                      )}
                    </div>
                    <pre className="whitespace-pre-wrap break-words font-sans text-sm">
                      {message.assistant_response}
                    </pre>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex justify-start">
                <div className="max-w-[80%] mr-12">
                  <div className="rounded-lg p-4 shadow-sm bg-red-100 dark:bg-red-950 border border-red-300 dark:border-red-800 rounded-tl-none">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <AlertCircle className="h-4 w-4" />
                      <span className="font-semibold">No Response Generated</span>
                    </div>
                    <p className="text-sm mt-2 italic opacity-60">
                      The assistant did not generate a response for this message.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Additional Metadata */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Metadata</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">Message ID</p>
              <p className="font-mono font-medium">{message.id}</p>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">Telegram User ID</p>
              <p className="font-mono font-medium">{message.telegram_user_id}</p>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">Chat ID</p>
              <p className="font-mono font-medium">{message.chat_id}</p>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">Response Time</p>
              <p className="font-mono font-medium">
                {message.response_time_ms ? `${message.response_time_ms}ms` : 'N/A'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminTelegramMessageDetails;
